import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ensureFresh, refresh as tbRefresh } from '../services/tbToken';
import './SwaasScoreGauge.css';

// Color thresholds
const getColorFromScore = (score) => {
  if (score >= 96) return 'green';   // 96-100: Excellent
  if (score >= 90) return 'amber';   // 90-95: Good
  return 'red';                       // < 90: Needs Attention
};

// API base URL and refresh interval
const API_BASE = process.env.REACT_APP_API_BASE !== undefined ? process.env.REACT_APP_API_BASE : 'http://localhost:3001';
const REFRESH_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

// Module-level cache for SWaaS scores (survives StrictMode remounts)
const scoreCache = new Map();

// Per-color Tailwind classes
const colorMap = {
  green: {
    card:        'bg-green-50 border-green-200',
    title:       'text-green-800',
    progress:    'bg-green-500',
    trackBg:     'bg-green-200/60',
    stroke:      '#22c55e',
    trackStroke: '#d1fae5',
    badge:       'bg-green-100 text-green-700',
    statusLabel: 'Excellent',
  },
  amber: {
    card:        'bg-amber-50 border-amber-200',
    title:       'text-amber-800',
    progress:    'bg-amber-500',
    trackBg:     'bg-amber-200/60',
    stroke:      '#f59e0b',
    trackStroke: '#fef3c7',
    badge:       'bg-amber-100 text-amber-700',
    statusLabel: 'Good',
  },
  red: {
    card:        'bg-red-50 border-red-200',
    title:       'text-red-800',
    progress:    'bg-red-500',
    trackBg:     'bg-red-200/60',
    stroke:      '#ef4444',
    trackStroke: '#fee2e2',
    badge:       'bg-red-100 text-red-700',
    statusLabel: 'Needs Attention',
  },
};

// Token handling: this component reads the live TB token from the singleton
// (services/tbToken) via ensureFresh() at call time, which refreshes proactively
// before expiry. It no longer takes an onTokenExpired callback — there is one
// token and one refresh path, owned by the singleton.
function SwaasScoreGauge({
  assetId,
  tbToken,
  washroomName,
  onDataStatus,
  washroomIcon,
}) {
  const [scoreData, setScoreData] = useState(() => {
    const cached = scoreCache.get(assetId);
    return cached?.result || null;
  });
  const [loading, setLoading] = useState(() => !scoreCache.get(assetId)?.result);
  const [showInfo, setShowInfo] = useState(false);
  const infoTimerRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const onDataStatusRef = useRef(onDataStatus);
  onDataStatusRef.current = onDataStatus;
  // Latest scoreData read inside fetch callbacks without making it an effect
  // dependency (which would re-fetch on every score change). Used by the
  // transient-401 keep-branch to decide cold-mount vs. has-data. (PR #40 review.)
  const scoreDataRef = useRef(scoreData);
  scoreDataRef.current = scoreData;
  // Guards async settles (fetch .then / interval) from updating state after
  // unmount. (PR #41 review.)
  const mountedRef = useRef(true);

  // Initial fetch effect
  useEffect(() => {
    if (!tbToken) return;

    // Apply a resolved fetch result, honouring the transient-401 keep-sentinel.
    // Shared by both .then handlers below (was duplicated). No-ops after unmount.
    const applyResult = (result) => {
      if (!mountedRef.current) return;
      if (result && result.__keep) {
        // Transient 401: keep an existing score; on a cold mount (no score yet)
        // clear loading and report unavailable so the tile isn't stuck invisible.
        setLoading(false);
        if (!scoreDataRef.current) onDataStatusRef.current?.(assetId, false);
        return;
      }
      setScoreData(result);
      setLoading(false);
      isInitialLoadRef.current = false;
      onDataStatusRef.current?.(assetId, !!result);
    };

    if (!assetId) {
      setScoreData(null);
      setLoading(false);
      onDataStatusRef.current?.(assetId, false);
      return;
    }

    const cached = scoreCache.get(assetId);

    if (cached?.result) {
      setScoreData(cached.result);
      setLoading(false);
      onDataStatusRef.current?.(assetId, !!cached.result);
      return;
    }

    if (cached?.promise) {
      cached.promise.then(applyResult);
      return;
    }

    const url = `${API_BASE}/api/swaas-score?assetId=${encodeURIComponent(assetId)}`;

    // Read the live token from the singleton at call time (ensureFresh refreshes
    // proactively before expiry) — never a stale prop copy. A 401 here should be
    // near-impossible; if it still happens it's the G-X identity case, so do one
    // single-flight refresh and treat it as transient (keep-sentinel).
    const promise = (async () => {
      try {
        const token = await ensureFresh();
        const response = await fetch(url, { headers: { 'X-TB-Token': token } });
        if (response.status === 401) {
          console.warn('[SwaasScoreGauge] 401 despite proactive refresh — single refresh attempt');
          tbRefresh();
          throw new Error('TOKEN_EXPIRED');
        }
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();

        if (result.ok && result.data.exists && result.data.score > 0) {
          const data = {
            score: result.data.score,
            color: result.data.color || getColorFromScore(result.data.score),
          };
          scoreCache.set(assetId, { result: data, timestamp: Date.now() });
          return data;
        }
        scoreCache.set(assetId, { result: null, timestamp: Date.now() });
        return null;
      } catch (err) {
        // Always clear the cache so the next effect run re-fetches instead of
        // awaiting this settled promise.
        scoreCache.delete(assetId);
        // A transient 401 (token refreshing) is NOT "no data". The keep-sentinel
        // leaves the gauge showing its last score instead of flipping to empty
        // and signalling onDataStatus(false) on a healthy washroom. Genuine
        // errors fall through to null. (PR #40 review.)
        if (err?.message === 'TOKEN_EXPIRED') return { __keep: true };
        return null;
      }
    })();

    scoreCache.set(assetId, { promise, result: null });

    promise.then(applyResult);
  }, [assetId, tbToken]);

  // Track mount state + clean up the info timer on unmount.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(infoTimerRef.current);
    };
  }, []);

  // Periodic refresh every 2 minutes
  useEffect(() => {
    if (!tbToken || !assetId) return;

    // Abort an in-flight tick on unmount/dep-change so a fetch doesn't run to
    // completion against a torn-down component. (PR #41 review nit.)
    const controller = new AbortController();
    const interval = setInterval(async () => {
      try {
        const url = `${API_BASE}/api/swaas-score?assetId=${encodeURIComponent(assetId)}`;
        const token = await ensureFresh();
        const response = await fetch(url, { headers: { 'X-TB-Token': token }, signal: controller.signal });
        // 401 should be near-impossible with proactive ensureFresh; if it still
        // happens (G-X), do one single-flight refresh and move on.
        if (response.status === 401) {
          console.warn('[SwaasScoreGauge] periodic 401 despite proactive refresh — single refresh attempt');
          tbRefresh();
          return;
        }
        if (!response.ok) return;
        const result = await response.json();

        if (result.ok && result.data.exists && result.data.score > 0) {
          const data = {
            score: result.data.score,
            color: result.data.color || getColorFromScore(result.data.score),
          };
          scoreCache.set(assetId, { result: data, timestamp: Date.now() });
          if (mountedRef.current) setScoreData(data); // guard async settle after unmount
        }
      } catch {
        // Silently fail on refresh errors
      }
    }, REFRESH_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      controller.abort();
    };
  }, [assetId, tbToken]);

  if (loading || !scoreData) return null;

  const { score, color } = scoreData;
  const c = colorMap[color] || colorMap.green;
  const displayScore = Math.round(score);
  const circumference = 2 * Math.PI * 42; // r=42 → ≈264
  const arcLength = (score / 100) * circumference;

  return (
    <>
      {/* Card */}
      <div
        onClick={() => {
          setShowInfo(true);
          clearTimeout(infoTimerRef.current);
          infoTimerRef.current = setTimeout(() => setShowInfo(false), 5000);
        }}
        role="button"
        tabIndex={0}
        aria-label="Tap to learn about SWaaS Score"
        className={`flex items-center gap-3 sm:gap-5 rounded-2xl sm:rounded-3xl border px-3 py-3 sm:px-5 sm:py-4 mx-2 sm:mx-3 cursor-pointer select-none transition-shadow duration-200 hover:shadow-md ${c.card}`}
        style={{ minWidth: 400, maxWidth: 520 }}
      >
        {/* Washroom icon */}
        {washroomIcon && (
          <div className="flex-shrink-0 flex items-center justify-center" style={{ width: 56, height: 56 }}>
            {washroomIcon}
          </div>
        )}
        {/* Circular gauge */}
        <div className="swaas-gauge__circle flex-shrink-0">
          <svg viewBox="0 0 100 100" className="swaas-gauge__svg">
            {/* Track */}
            <circle cx="50" cy="50" r="42" fill="none" stroke={c.trackStroke} strokeWidth="9" />
            {/* Arc */}
            <circle
              cx="50" cy="50" r="42"
              fill="none"
              stroke={c.stroke}
              strokeWidth="9"
              strokeLinecap="round"
              strokeDasharray={`${arcLength} ${circumference + 1}`}
              transform="rotate(-90 50 50)"
              className="swaas-gauge__arc"
            />
          </svg>
          <span className="swaas-gauge__score" style={{ color: c.stroke }}>
            {displayScore}
          </span>
        </div>

        {/* Right: title + status badge + washroom name + progress bar */}
        <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
          <p className={`font-extrabold text-sm sm:text-base leading-tight ${c.title}`}>
            SWaaS Score 🌿
          </p>
          <p className="text-xs text-gray-500 break-words leading-snug">
            {washroomName}
          </p>
          <span className={`items-center px-2 py-0.5 rounded-full text-xs font-semibold tracking-wide ${c.badge}`}>
            {c.statusLabel}
          </span>
        </div>
      </div>

      {/* Info Modal */}
      {showInfo && createPortal(
        <div className="swaas-info-overlay" onClick={() => { clearTimeout(infoTimerRef.current); setShowInfo(false); }}>
          <div className="swaas-info-modal" onClick={(e) => e.stopPropagation()}>
            <button className="swaas-info-close" onClick={() => { clearTimeout(infoTimerRef.current); setShowInfo(false); }}>✕</button>
            <h3 className="swaas-info-title">What is SWaaS 🌿 Score?</h3>
            <p className="swaas-info-text">
              The <strong>SWaaS Score</strong> measures <strong>S</strong>anitation,{' '}
              <strong>W</strong>ellness &amp; <strong>S</strong>mell. It tracks air freshness,
              cleanliness, your feedback, and maintenance response to show how well this
              washroom is kept.
            </p>
            <p className="swaas-info-highlight">Higher score = Cleaner washroom!</p>
            <div className="swaas-info-scale">
              <div className="swaas-info-scale-item swaas-info-scale--green">
                <span className="swaas-info-scale-range">96–100</span>
                <span className="swaas-info-scale-label">Excellent</span>
              </div>
              <div className="swaas-info-scale-item swaas-info-scale--amber">
                <span className="swaas-info-scale-range">90–95</span>
                <span className="swaas-info-scale-label">Good</span>
              </div>
              <div className="swaas-info-scale-item swaas-info-scale--red">
                <span className="swaas-info-scale-range">&lt;90</span>
                <span className="swaas-info-scale-label">Needs Attention</span>
              </div>
            </div>
            <p className="swaas-info-footer">Your feedback helps us maintain high standards!</p>
          </div>
        </div>
      , document.body)}
    </>
  );
}

export default SwaasScoreGauge;
